/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Skeleton } from "primereact/skeleton";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import {
  Boton,
  Titulo,
  CampoInformacion,
  Formulario,
  accessAPI,
  Sidebar,
  tituloBase,
  TablaFiltrable
} from "../../../shared";

// Ficha de una acción para el cliente: una card de detalles editable (los
// cuatro campos) y otra con los tipos de certificado vinculados. La
// vinculación muchos-a-muchos se maneja desde acá, no desde el tipo.
export default function AccionClienteIndividual() {
  const [accion, setAccion] = useState(null);
  // Cuál de los dos formularios está abierto: "detalles" o "tipos"
  const [sidebarAbierto, setSidebarAbierto] = useState(null);
  const [formLoader, setFormLoader] = useState(false);
  const [formKey, setFormKey] = useState(0);

  const { accionId } = useParams();
  const toast = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    accessAPI(
      "GET",
      `admin/config/accionescliente/${accionId}`,
      null,
      (respuesta) => {
        setAccion(respuesta);
        document.title = respuesta.nombre;
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar la acción",
          sticky: true
        });
      }
    );
    return () => (document.title = tituloBase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function editarDetalles(contenidoCampos) {
    setFormLoader(true);
    accessAPI(
      "PUT",
      `admin/config/accionescliente/${accionId}`,
      contenidoCampos,
      (respuesta) => {
        setAccion((prev) => ({ ...prev, ...respuesta }));
        document.title = respuesta.nombre;
        setSidebarAbierto(null);
        setFormLoader(false);
        toast.current?.show({
          severity: "success",
          summary: "Actualizado",
          detail: "Acción actualizada correctamente",
          life: 3000
        });
      },
      (respuesta) => {
        setFormLoader(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al actualizar la acción",
          sticky: true
        });
      }
    );
  }

  // Vincula un tipo de certificado a la acción
  function agregarTipo(contenidoCampos) {
    setFormLoader(true);
    accessAPI(
      "POST",
      `admin/config/accionescliente/${accionId}/certificadotipos`,
      { certificadoTipoId: contenidoCampos.certificadoTipoId },
      (respuesta) => {
        setAccion((prev) => ({
          ...prev,
          certificadotipo_x_accioncliente: [
            ...(prev.certificadotipo_x_accioncliente || []),
            respuesta
          ]
        }));
        setSidebarAbierto(null);
        setFormLoader(false);
        toast.current?.show({
          severity: "success",
          summary: "Agregado",
          detail: "Tipo de certificado vinculado correctamente",
          life: 3000
        });
      },
      (respuesta) => {
        setFormLoader(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al vincular el tipo",
          sticky: true
        });
      }
    );
  }

  // Desvincula un tipo de certificado de la acción
  function eliminarTipo(vinculo) {
    accessAPI(
      "DELETE",
      `admin/config/accionescliente/${accionId}/certificadotipos/${vinculo.certificadotipo.id}`,
      null,
      () => {
        setAccion((prev) => ({
          ...prev,
          certificadotipo_x_accioncliente: (
            prev.certificadotipo_x_accioncliente || []
          ).filter((v) => v.id !== vinculo.id)
        }));
        toast.current?.show({
          severity: "success",
          summary: "Eliminado",
          detail: "Tipo de certificado desvinculado correctamente",
          life: 3000
        });
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al desvincular el tipo",
          sticky: true
        });
      }
    );
  }

  function eliminarAccion() {
    accessAPI(
      "DELETE",
      `admin/config/accionescliente/${accionId}`,
      null,
      () => {
        navigate("/config/accionescliente");
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al eliminar la acción",
          sticky: true
        });
      }
    );
  }

  const detallesFormConfig = accion
    ? {
        secciones: [
          {
            campos: [
              {
                nombre: "nombre",
                tipoitemformularioid: 1,
                etiqueta: "Nombre",
                requerido: true,
                ayuda:
                  "Es el título de la sección en el panel del cliente.",
                respuesta: accion.nombre
              },
              {
                nombre: "etiqueta",
                tipoitemformularioid: 1,
                etiqueta: "Etiqueta",
                ayuda:
                  "Es la etiqueta que se ve en el listado de clientes. Vacía, la acción no aparece en el listado.",
                respuesta: accion.etiqueta
              },
              {
                nombre: "texto",
                tipoitemformularioid: 11,
                etiqueta: "Texto",
                ayuda:
                  "Se muestra en el sidebar de operaciones del portal del cliente. Admite [b]negrita[/b], [i]cursiva[/i] y [r]rojo[/r].",
                respuesta: accion.texto
              },
              {
                nombre: "link",
                tipoitemformularioid: 1,
                etiqueta: "Link",
                ayuda:
                  "Botón que acompaña al texto. Puede ser una URL completa, que abre en otra pestaña, o una ruta de la plataforma como /generadoretiquetasjuguetes.",
                respuesta: accion.link
              },
              {
                nombre: "textoBoton",
                tipoitemformularioid: 1,
                etiqueta: "Texto del botón",
                ayuda:
                  'Lo que dice el botón del link, por ejemplo "Iniciar trámite en URSEA". Vacío, muestra el nombre de la acción.',
                respuesta: accion.textoBoton
              }
            ]
          }
        ],
        botones: [
          {
            etiqueta: "Guardar",
            callback: editarDetalles,
            validar: true
          }
        ]
      }
    : null;

  const agregarTipoFormConfig = {
    secciones: [
      {
        campos: [
          {
            nombre: "certificadoTipoId",
            tipoitemformularioid: 5, // dropdown
            etiqueta: "Tipo de certificado",
            opciones: "admin/config/certificadotipo",
            sinOpcionInicial: true,
            requerido: true,
            ayuda:
              "Un cliente con un proceso activo de este tipo ve esta acción en su panel, una sola vez aunque tenga varios procesos que correspondan. También es su etiqueta en el listado de clientes."
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Agregar tipo",
        callback: agregarTipo,
        validar: true
      }
    ]
  };

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <ConfirmDialog />

      {!accion && (
        <div
          css={{
            marginTop: "2em",
            display: "flex",
            flexDirection: "column",
            gap: "1em"
          }}
        >
          <Skeleton width="20rem" height="2.5rem" />
          <Card>
            <Skeleton width="100%" height="8rem" />
          </Card>
          <Card>
            <Skeleton width="100%" height="5rem" />
          </Card>
        </div>
      )}

      {accion && (
        <>
          <Titulo
            urlVolver="/config/accionescliente"
            botones={
              <Boton
                rojo
                transparente
                callback={() => {
                  confirmDialog({
                    message:
                      "¿Está seguro que desea eliminar esta acción? Se desvinculará de todos los tipos de certificado.",
                    header: "Confirmar eliminación",
                    icon: "pi pi-exclamation-triangle",
                    acceptLabel: "Eliminar",
                    rejectLabel: "Cancelar",
                    acceptClassName: "p-button-danger",
                    accept: eliminarAccion
                  });
                }}
              >
                Eliminar
              </Boton>
            }
          >
            {accion.nombre}
          </Titulo>

          {/* Detalles de la acción */}
          <Card>
            <Titulo
              botones={
                <Boton
                  icono
                  negro
                  callback={() => {
                    setFormKey((k) => k + 1);
                    setSidebarAbierto("detalles");
                  }}
                >
                  <i className="pi pi-pencil" />
                </Boton>
              }
            >
              Detalles
            </Titulo>
            <div className="grid">
              <CampoInformacion nombreCampo="Nombre">
                {accion.nombre}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Etiqueta en el listado de clientes">
                {accion.etiqueta || "—"}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Link">
                {accion.link || "—"}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Texto del botón">
                {accion.textoBoton || "—"}
              </CampoInformacion>
            </div>
            <div className="grid">
              <CampoInformacion nombreCampo="Texto" anchoCompleto>
                <span css={{ whiteSpace: "pre-wrap" }}>
                  {accion.texto || "—"}
                </span>
              </CampoInformacion>
            </div>
          </Card>

          {/* Tipos de certificado vinculados */}
          <Card css={{ marginTop: "1em" }}>
            <Titulo
              botones={
                <Boton
                  icono
                  negro
                  callback={() => {
                    setFormKey((k) => k + 1);
                    setSidebarAbierto("tipos");
                  }}
                >
                  <i className="pi pi-plus" />
                </Boton>
              }
            >
              Tipos de certificado
            </Titulo>
            <TablaFiltrable conFiltros={false}
              value={accion.certificadotipo_x_accioncliente || []}
              emptyMessage="Esta acción no está vinculada a ningún tipo de certificado"
              size="small"
              nombreArchivo="tipos_certificado_accion"
            >
              <Column
                header="Tipo de certificado"
                body={(vinculo) =>
                  vinculo.certificadotipo?.procesotipo?.nombre
                    ? `${vinculo.certificadotipo.procesotipo.nombre} - ${vinculo.certificadotipo?.nombre}`
                    : vinculo.certificadotipo?.nombre
                }
              />
              <Column
                body={(vinculo) => (
                  <Boton
                    icono
                    rojo
                    chico
                    confirmacion="¿Desvincular este tipo de certificado?"
                    callback={() => eliminarTipo(vinculo)}
                  >
                    <i className="pi pi-trash" />
                  </Boton>
                )}
                style={{ width: "3rem", textAlign: "center" }}
              />
            </TablaFiltrable>
          </Card>

          <Sidebar
            visible={sidebarAbierto === "detalles"}
            position="right"
            onHide={() => setSidebarAbierto(null)}
            className="w-full lg:w-30rem md:w-30rem"
          >
            <Titulo>Editar acción</Titulo>
            {detallesFormConfig && (
              <Formulario
                key={`detalles-${formKey}`}
                config={detallesFormConfig}
                loader={formLoader}
              />
            )}
          </Sidebar>

          <Sidebar
            visible={sidebarAbierto === "tipos"}
            position="right"
            onHide={() => setSidebarAbierto(null)}
            className="w-full lg:w-30rem md:w-30rem"
          >
            <Titulo>Agregar tipo de certificado</Titulo>
            <Formulario
              key={`tipos-${formKey}`}
              config={agregarTipoFormConfig}
              loader={formLoader}
            />
          </Sidebar>
        </>
      )}
    </>
  );
}
